import React from 'react';

import { Report } from 'aws-sdk/clients/d3aservice';
import { Container, CopyToClipboard, Header, KeyValuePairs, SpaceBetween, Tabs } from '@amzn/awsui-components-react-v3';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';
import { REPORT_TYPE_D3A_DIFF, REPORT_TYPE_D3A } from 'src/commons/constants';
import { Link } from 'src/components/common/Link';
import { createReportDetailLink } from 'src/routes';

export interface ReportDetailProps {
  setContentType: any;
  activeGroup: string;
  activeWorkspace?: any;
  setNotifications?: any;
  report: Report;
  s3Urls: string[];
  sourceReport1?: Report;
  sourceReport2?: Report;
}

export const ReportDetail = (props: ReportDetailProps) => {
  const detailContainer = () => {
    return (
      <Container header={<Header variant='h2'>Report details</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Report ID',
              value: props.report.reportId,
            },
            {
              label: 'Report type',
              value: props.report.type || REPORT_TYPE_D3A,
            },
            {
              label: 'Status',
              value: StatusIcon(props.report.status),
            },
            {
              label: 'Status reason',
              value: props.report.statusReason,
            },
            {
              label: 'Report name',
              value: props.report.name,
            },
            {
              label: 'Report description',
              value: props.report.description,
            },
            {
              label: 'Owner ID',
              value: props.report.ownerId,
            },
            {
              label: 'Created by',
              value: props.report.createdBy,
            },
            {
              label: 'Created on',
              value: props.report.createdOn,
            },
            {
              label: 'Generation time (mins)',
              value: props.report.generationTimeMins,
            },
            {
              label: 'Generation start time',
              value: props.report.generationStartTime,
            },
            {
              label: 'Generation end time',
              value: props.report.generationEndTime,
            },
          ]}
        />
      </Container>
    );
  };

  const configContainer = () => {
    // Check if this is a diff report
    const isDiffReport = props.report.type === REPORT_TYPE_D3A_DIFF;

    if (isDiffReport) {
      const report1 = props.sourceReport1;
      const report2 = props.sourceReport2;

      return (
        <Container header={<Header variant='h2'>Diff report configuration</Header>}>
          <SpaceBetween size='l'>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <Container header={<Header variant='h4'>Report 1</Header>}>
                <KeyValuePairs
                  columns={1}
                  items={[
                    {
                      label: 'ID',
                      value: props.report.diffReportConfig?.reportId1 ? (
                        <Link href={createReportDetailLink(props.report.diffReportConfig.reportId1)}>
                          {props.report.diffReportConfig.reportId1}
                        </Link>
                      ) : (
                        '-'
                      ),
                    },
                    {
                      label: 'Name',
                      value: report1?.name || '-',
                    },
                    {
                      label: 'Description',
                      value: report1?.description || '-',
                    },
                    {
                      label: 'Created on',
                      value: report1?.createdOn || '-',
                    },
                    {
                      label: 'Access provider',
                      value: report1?.config?.accessGrantFilter?.accessProvider || '-',
                    },
                    {
                      label: 'Governance tag',
                      value: report1?.config?.accessGrantFilter?.dgsa || '-',
                    },
                  ]}
                />
              </Container>
              <Container header={<Header variant='h4'>Report 2</Header>}>
                <KeyValuePairs
                  columns={1}
                  items={[
                    {
                      label: 'ID',
                      value: props.report.diffReportConfig?.reportId2 ? (
                        <Link href={createReportDetailLink(props.report.diffReportConfig.reportId2)}>
                          {props.report.diffReportConfig.reportId2}
                        </Link>
                      ) : (
                        '-'
                      ),
                    },
                    {
                      label: 'Name',
                      value: report2?.name || '-',
                    },
                    {
                      label: 'Description',
                      value: report2?.description || '-',
                    },
                    {
                      label: 'Created on',
                      value: report2?.createdOn || '-',
                    },
                    {
                      label: 'Access provider',
                      value: report2?.config?.accessGrantFilter?.accessProvider || '-',
                    },
                    {
                      label: 'Governance tag',
                      value: report2?.config?.accessGrantFilter?.dgsa || '-',
                    },
                  ]}
                />
              </Container>
            </div>
          </SpaceBetween>
        </Container>
      );
    }

    // Regular report configuration
    return (
      <Container header={<Header variant='h2'>Report configuration</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Output types',
              value: props.report.config.outputTypes?.join(', '),
            },
            props.report.config.accessGrantFilter?.dgsa
              ? {
                  label: 'Governance tag',
                  value: props.report.config.accessGrantFilter?.dgsa,
                }
              : undefined,
            props.report.config.accessGrantFilter?.accessProvider
              ? {
                  label: 'Access provider',
                  value: props.report.config.accessGrantFilter?.accessProvider,
                }
              : undefined,
            props.report.config.accessGrantFilter?.resourceArn
              ? {
                  label: 'Resource ARN',
                  value: props.report.config.accessGrantFilter?.resourceArn,
                }
              : undefined,
            props.report.config.accessGrantFilter?.status
              ? {
                  label: 'Grant status',
                  value: props.report.config.accessGrantFilter?.status,
                }
              : undefined,
            props.report.config.accessGrantFilter?.createDateRange?.startDate
              ? {
                  label: 'Include access grants with decision date after',
                  value: props.report.config.accessGrantFilter?.createDateRange?.startDate,
                }
              : undefined,
            props.report.config.accessGrantFilter?.createDateRange?.startDate
              ? {
                  label: 'Include access grants with decision date before',
                  value: props.report.config.accessGrantFilter?.createDateRange?.endDate,
                }
              : undefined,
          ].filter((item) => item != undefined)}
        />
      </Container>
    );
  };

  const s3MetadataContainer = () => {
    // Check if this is a diff report
    const isDiffReport = props.report.type === REPORT_TYPE_D3A_DIFF;

    if (isDiffReport) {
      return (
        <Container header={<Header variant='h2'>S3 artifacts</Header>}>
          <KeyValuePairs
            columns={1}
            items={[
              {
                label: 'Processed diff report S3 path',
                value:
                  props.report.s3Metadata?.d3aProcessedS3PathsMap &&
                  Object.keys(props.report.s3Metadata.d3aProcessedS3PathsMap).length > 0 ? (
                    <CopyToClipboard
                      variant={'inline'}
                      textToCopy={Object.values(props.report.s3Metadata.d3aProcessedS3PathsMap)[0]}
                      copySuccessText={'Copied S3 path'}
                      copyErrorText={'Failed to copy S3 path'}
                    />
                  ) : (
                    '-'
                  ),
              },
            ]}
          />
        </Container>
      );
    }

    // Regular report S3 metadata
    return (
      <Container header={<Header variant='h2'>S3 artifacts</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            {
              label: 'Raw access grants S3 path',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={props.report.s3Metadata.d3aAccessGrantsExportS3Path}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
            {
              label: 'Staged report S3 path',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={Object.values(props.report.s3Metadata.d3aStagedS3PathsMap).join(', ')}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
            {
              label: 'Processed report S3 paths',
              value: (
                <CopyToClipboard
                  variant={'inline'}
                  textToCopy={Object.values(props.report.s3Metadata.d3aProcessedS3PathsMap).join(', ')}
                  copySuccessText={'Copied S3 path'}
                  copyErrorText={'Failed to copy S3 path'}
                />
              ),
            },
          ].filter((item) => item != undefined)}
        />
      </Container>
    );
  };

  return (
    <Tabs
      tabs={[
        {
          id: 'detail',
          label: 'Details',
          content: detailContainer(),
        },
        {
          id: 'config',
          label: 'Configuration',
          content: configContainer(),
        },
        {
          id: 'artifacts',
          label: 'Artifacts',
          content: (
            <SpaceBetween size={'l'} direction={'vertical'}>
              {s3MetadataContainer()}
            </SpaceBetween>
          ),
        },
      ]}
    />
  );
};
